import axiosInstance from '../lib/axios';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createRoot } from 'react-dom/client';
import { logout } from '../../store/actions/authActions';
import useAuth from './useAuth';
import Loader from '../../components/UI/Loader';

// The Loader overlay is mounted into a single shared container/root so every
// component calling this hook shows the same overlay instance instead of
// stacking one per hook instance.
let loaderRoot = null;
const getLoaderRoot = () => {
	if (!loaderRoot) {
		let container = document.getElementById('axios-loader-root');
		if (!container) {
			container = document.createElement('div');
			container.id = 'axios-loader-root';
			document.body.appendChild(container);
		}
		loaderRoot = createRoot(container);
	}
	return loaderRoot;
};

const useAxiosPrivate = () => {
	const { auth } = useAuth();
	const dispatch = useDispatch();

	useEffect(() => {
		const showLoader = () => getLoaderRoot().render(<Loader message="Loading" />);
		const hideLoader = () => getLoaderRoot().render(null);

		// Track number of ongoing requests
		let requestCount = 0;
		let loadingStartTime = null;
		const MIN_LOADING_TIME = 300; // ms
		const GRACE_PERIOD = 200; // ms
		let graceTimeout = null;

		const clearGraceTimeout = () => {
			if (graceTimeout) {
				clearTimeout(graceTimeout);
				graceTimeout = null;
			}
		};

		const updateProgress = () => {
			requestCount--;
			if (requestCount <= 0) {
				requestCount = 0;
				const now = Date.now();
				const elapsed = loadingStartTime ? now - loadingStartTime : 0;
				clearGraceTimeout();
				graceTimeout = setTimeout(() => {
					if (requestCount === 0) {
						if (elapsed < MIN_LOADING_TIME) {
							setTimeout(() => {
								hideLoader();
								loadingStartTime = null;
							}, MIN_LOADING_TIME - elapsed);
						} else {
							hideLoader();
							loadingStartTime = null;
						}
					}
				}, GRACE_PERIOD);
			}
		};

		const requestIntercept = axiosInstance.interceptors.request.use(
			config => {
				clearGraceTimeout();
				requestCount++;
				if (requestCount === 1) {
					showLoader();
					loadingStartTime = Date.now();
				}
				const token = auth?.accessToken || sessionStorage.getItem('accessToken');
				// attach Authorization header if not present
				if (!config.headers['Authorization'] && token) {
					config.headers['Authorization'] = `Bearer ${token}`;
				}
				return config;
			},
			error => {
				updateProgress();
				return Promise.reject(error);
			}
		);

		const responseIntercept = axiosInstance.interceptors.response.use(
			function (response) {
				updateProgress();
				return response;
			},
			function (error) {
				updateProgress();
				const isUnauthorized = error?.response?.status === 401;
				const isNotLoginPage = window.location.pathname !== '/';
				const isNotChangePassword = !error?.config?.url?.includes('user/change_password');

				if (isUnauthorized && isNotLoginPage && isNotChangePassword) {
					dispatch(logout());
					window.location.href = '/';
				}

				return Promise.reject(error);
			}
		);

		return () => {
			axiosInstance.interceptors.request.eject(requestIntercept);
			axiosInstance.interceptors.response.eject(responseIntercept);
			// Reset progress and counter on cleanup
			requestCount = 0;
			clearGraceTimeout();
			hideLoader();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [auth]);

	return axiosInstance;
};

export default useAxiosPrivate;
